import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addEquipment, getAllEquipment } from '../redux/slices/equipmentsSlice';
import { toast } from 'react-toastify';
import { LoadingModal } from './LoadingModal';

const BulkAddEquipmentModal = ({ setBulkAddModal, setAddEquipmentModal }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, errors: [], successes: [] });
  const isLoading = useSelector((state) => state.equipments.loading);
  const error = useSelector((state) => state.equipments.error);

  // Simple CSV parser (same as BulkUpdateEquipmentModal)
  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    if (lines.length < 2) {
      throw new Error('CSV file must contain at least a header row and one data row');
    }

    // Parse header
    const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));

    // Parse data rows
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines

      // Handle CSV with quoted fields that may contain commas
      const values = [];
      let currentValue = '';
      let insideQuotes = false;

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim()); // Add last value

      if (values.length !== headers.length) {
        console.warn(`Row ${i + 1} has ${values.length} columns but header has ${headers.length}`);
        continue;
      }

      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index]?.replace(/^"|"$/g, '') || '';
      });
      rows.push(row);
    }

    return rows;
  };

  // Convert CSV row to equipment add format (no Equipment ID needed)
  const csvRowToEquipmentData = (row) => {
    const equipmentData = {};

    // Map CSV columns to equipment fields
    // Required fields must be present
    if (!row['Name (English)']?.trim()) {
      throw new Error('Name (English) is required');
    }
    if (!row['Category (English)']?.trim()) {
      throw new Error('Category (English) is required');
    }
    if (!row['Description (English)']?.trim()) {
      throw new Error('Description (English) is required');
    }
    if (!row['Quantity']?.trim()) {
      throw new Error('Quantity is required');
    }
    if (!row['Rental Price']?.trim()) {
      throw new Error('Rental Price is required');
    }
    if (!row['Location']?.trim()) {
      throw new Error('Location is required');
    }
    if (!row['Self Transport']?.trim()) {
      throw new Error('Self Transport is required');
    }
    if (!row['Specs']?.trim()) {
      throw new Error('Specs is required');
    }
    if (!row['Tutorials']?.trim()) {
      throw new Error('Tutorials is required');
    }

    // Set required fields
    equipmentData.name = row['Name (English)'].trim();
    equipmentData.category = row['Category (English)'].trim();
    equipmentData.description = row['Description (English)'].trim();

    const qty = parseInt(row['Quantity'].trim(), 10);
    if (isNaN(qty)) {
      throw new Error('Quantity must be a valid number');
    }
    equipmentData.qty = qty;

    const price = parseFloat(row['Rental Price'].trim());
    if (isNaN(price)) {
      throw new Error('Rental Price must be a valid number');
    }
    equipmentData.rentalPrice = price;

    equipmentData.location = row['Location'].trim();
    equipmentData.selfTransport = row['Self Transport'].trim();
    equipmentData.specs = row['Specs'].trim();
    equipmentData.tutorials = row['Tutorials'].trim();

    // Optional fields
    if (row['Availability']?.trim()) {
      equipmentData.availability = row['Availability'].trim().toUpperCase() === 'YES';
    } else {
      equipmentData.availability = true; // Default to available
    }

    if (row['Images']?.trim()) {
      // Images are semicolon-separated in CSV
      const images = row['Images']
        .split(';')
        .map((img) => img.trim())
        .filter((img) => img);
      if (images.length > 0) {
        equipmentData.images = images;
      } else {
        equipmentData.images = [];
      }
    } else {
      equipmentData.images = [];
    }

    // Build translations object - English is required
    const translations = {
      en: {
        name: row['Name (English)'].trim(),
        category: row['Category (English)'].trim(),
        description: row['Description (English)'].trim(),
      },
    };

    // Include Spanish translations if any Spanish field has a value
    if (
      row['Name (Spanish)']?.trim() ||
      row['Category (Spanish)']?.trim() ||
      row['Description (Spanish)']?.trim()
    ) {
      translations.es = {
        name: row['Name (Spanish)']?.trim() || '',
        category: row['Category (Spanish)']?.trim() || '',
        description: row['Description (Spanish)']?.trim() || '',
      };
    }

    equipmentData.translations = translations;

    return equipmentData;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleBulkAdd = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setIsProcessing(true);
    setProgress({ current: 0, total: 0, errors: [], successes: [] });

    try {
      // Read file
      const fileText = await selectedFile.text();

      // Parse CSV
      let rows;
      try {
        rows = parseCSV(fileText);
      } catch (parseError) {
        toast.error(`Error parsing CSV: ${parseError.message}`);
        setIsProcessing(false);
        return;
      }

      if (rows.length === 0) {
        toast.error('No data rows found in CSV file');
        setIsProcessing(false);
        return;
      }

      setProgress((prev) => ({ ...prev, total: rows.length }));

      // Process each row
      const errors = [];
      const successes = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        setProgress((prev) => ({ ...prev, current: i + 1 }));

        try {
          const equipmentData = csvRowToEquipmentData(row);

          // Add equipment
          await dispatch(addEquipment(equipmentData)).unwrap();

          successes.push({
            row: i + 2, // +2 because row 1 is header, and we're 0-indexed
            name: equipmentData.name,
          });
        } catch (error) {
          errors.push({
            row: i + 2,
            error: error.message || 'Unknown error',
          });
        }
      }

      setProgress((prev) => ({ ...prev, errors, successes }));

      // Show summary
      if (errors.length === 0) {
        toast.success(`Successfully added ${successes.length} equipment item(s)!`);
        // Refresh equipment list
        dispatch(getAllEquipment());
        setBulkAddModal(false);
        setAddEquipmentModal(false);
      } else if (successes.length > 0) {
        toast.warning(
          `Added ${successes.length} item(s), but ${errors.length} item(s) failed. Check the details below.`
        );
      } else {
        toast.error(`Failed to add all ${errors.length} item(s). Check the details below.`);
      }
    } catch (error) {
      toast.error(`Error processing file: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setBulkAddModal(false);
      setSelectedFile(null);
      setProgress({ current: 0, total: 0, errors: [], successes: [] });
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      {(isLoading || error) && isProcessing && (
        <LoadingModal
          message={`Processing additions... ${progress.current}/${progress.total}`}
          error={error}
          onClose={null}
        />
      )}
      <div className="bg-white p-6 rounded-lg w-1/2 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <h3 className="text-lg font-bold mb-4">Bulk Add Equipment</h3>

        <div className="overflow-y-auto flex-grow pr-4 space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Upload a CSV file with equipment information. Each row will be added as a new piece of
              equipment. Required fields: Name (English), Category (English), Description (English),
              Quantity, Rental Price, Location, Self Transport, Specs, Tutorials.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={isProcessing}
            />
            {selectedFile && (
              <p className="text-sm text-green-600 mt-2">Selected: {selectedFile.name}</p>
            )}
          </div>

          {isProcessing && progress.total > 0 && (
            <div className="bg-blue-50 p-4 rounded-md">
              <p className="text-sm font-semibold mb-2">
                Processing: {progress.current} of {progress.total} equipment items
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {!isProcessing && progress.errors.length > 0 && (
            <div className="bg-red-50 p-4 rounded-md">
              <p className="text-sm font-semibold text-red-800 mb-2">
                Errors ({progress.errors.length}):
              </p>
              <div className="max-h-40 overflow-y-auto">
                {progress.errors.map((err, idx) => (
                  <p key={idx} className="text-xs text-red-600">
                    Row {err.row}: {err.error}
                  </p>
                ))}
              </div>
            </div>
          )}

          {!isProcessing && progress.successes.length > 0 && (
            <div className="bg-green-50 p-4 rounded-md">
              <p className="text-sm font-semibold text-green-800 mb-2">
                Successfully Added ({progress.successes.length}):
              </p>
              <div className="max-h-40 overflow-y-auto">
                {progress.successes.map((success, idx) => (
                  <p key={idx} className="text-xs text-green-600">
                    Row {success.row}: {success.name}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={handleClose}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-md"
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleBulkAdd}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md"
            disabled={!selectedFile || isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Upload and Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { BulkAddEquipmentModal };
